<template>
  <div class="view-container">
    <div class="search-panel">
      <div class="search-row">
        <div class="search-item">
          <label><i class="fas fa-project-diagram"></i> 流程名:</label>
          <input v-model="searchForm.processName" type="text" placeholder="请输入流程名" list="processNameList" class="form-control" @focus="onProcessNameFocus" autocomplete="off">
          <datalist id="processNameList">
            <option v-for="name in processNameOptions" :key="name" :value="name">{{ name }}</option>
          </datalist>
        </div>
        <div class="search-item">
          <label><i class="fas fa-hashtag"></i> 任务号:</label>
          <input v-model="searchForm.taskNo" type="text" placeholder="请输入任务号" class="form-control" autocomplete="off">
        </div>
        <div class="search-item">
          <label><i class="fas fa-align-left"></i> 摘要信息:</label>
          <input v-model="searchForm.summary" type="text" placeholder="请输入摘要信息" class="form-control" style="width:250px" autocomplete="off">
        </div>
        <div class="search-item">
          <label><i class="fas fa-flag"></i> 优先级:</label>
          <select v-model="searchForm.priority" class="form-control" style="width:90px">
            <option value="">全部</option>
            <option value="高">高</option>
            <option value="中">中</option>
            <option value="低">低</option>
          </select>
        </div>
      </div>
      <div class="search-row">
        <div class="search-item">
          <label><i class="fas fa-list-ol"></i> 流水号:</label>
          <input v-model="searchForm.serialNo" type="text" placeholder="请输入流水号" class="form-control" autocomplete="off">
        </div>
        <div class="search-item">
          <label><i class="fas fa-user-plus"></i> 发起人:</label>
          <input v-model="searchForm.initiator" type="text" placeholder="请输入发起人" class="form-control" autocomplete="off">
        </div>
        <div class="search-item">
          <label><i class="fas fa-calendar-plus"></i> 发起时间:</label>
          <input v-model="searchForm.initiateTimeStart" type="date" class="form-control">
          <span class="date-separator">至</span>
          <input v-model="searchForm.initiateTimeEnd" type="date" class="form-control">
        </div>
        <div class="search-item">
          <label><i class="fas fa-info-circle"></i> 状态:</label>
          <select v-model="searchForm.status" class="form-control">
            <option value="">全部</option>
            <option value="0">运行中</option>
            <option value="1">暂停中</option>
            <option value="2">已停止</option>
            <option value="3">已中止</option>
            <option value="4">已审批</option>
            <option value="5">拒绝结束</option>
            <option value="6">已删除</option>
            <option value="10014">加签中(并行)</option>
            <option value="10025">加签中(串行)</option>
            <option value="10018">询问</option>
            <option value="10003">已拒绝</option>
          </select>
        </div>
        <div class="search-actions">
          <button @click="handleSearch" class="btn btn-primary">
            <i class="fas fa-search"></i> 查询
          </button>
          <button @click="resetSearch" class="btn btn-default">
            <i class="fas fa-undo"></i> 重置
          </button>
          <button @click="exportTable" class="btn btn-success">
            <i class="fas fa-download"></i> 导出
          </button>
        </div>
      </div>
    </div>

    <div class="table-wrapper">
      <div class="table-header-row">
        <div class="table-header-left"></div>
        <div class="table-header-right">
          <button @click="toggleColumnSelector" class="btn btn-default btn-sm column-settings-btn"><i class="fas fa-columns"></i> 列设置</button>
        </div>
      </div>

      <div class="table-container">
        <table class="data-table">
          <thead>
            <tr>
              <th
                v-for="column in visibleColumns"
                :key="column.key"
                :class="{ 'sortable': column.sortable, 'sorted-asc': sortColumn === column.key && sortOrder === 'asc', 'sorted-desc': sortColumn === column.key && sortOrder === 'desc' }"
                @click="handleSort(column.key)"
                class="column-header"
                :draggable="true"
                @dragstart="dragStart($event, column)"
                @dragover.prevent
                @drop="dropColumn($event, column)"
                :style="column.width ? { width: column.width, minWidth: column.width } : {}"
              >
                <span>{{ column.label }}</span>
                <i v-if="column.sortable" class="fas" :class="sortColumn === column.key ? (sortOrder === 'asc' ? 'fa-chevron-up' : 'fa-chevron-down') : 'fa-sort'"></i>
              </th>
              <th class="action-column">操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="task in paginatedTasks" :key="task.id" @dblclick="viewTask(task)" class="task-row">
              <td v-for="column in visibleColumns" :key="column.key" :style="column.width ? { width: column.width, minWidth: column.width } : {}">
                <template v-if="column.key === 'status'">
                  <span class="status-badge" :class="getStatusClass(task)">{{ task.status }}</span>
                </template>
                <template v-else-if="column.key === 'priority'">
                  <span class="priority-badge" :class="task.priority === '高' ? 'high' : task.priority === '中' ? 'medium' : 'low'">{{ task.priority }}</span>
                </template>
                <template v-else-if="column.key === 'summary'">
                  <span class="summary-cell">{{ task[column.key] }}</span>
                </template>
                <template v-else>
                  {{ task[column.key] }}
                </template>
              </td>
              <td class="action-column">
                <button v-if="canRetrieve(task)" @click="retrieveTaskHandler(task)" class="btn btn-warning btn-xs" title="取回">
                  <i class="fas fa-undo"></i>
                </button>
                <button v-if="canDelete(task)" @click="deleteTask(task)" class="btn btn-danger btn-xs" title="删除">
                  <i class="fas fa-trash"></i>
                </button>
                <button @click="viewTask(task)" class="btn btn-info btn-xs" title="查看">
                  <i class="fas fa-eye"></i>
                </button>
              </td>
            </tr>
            <tr v-if="paginatedTasks.length === 0">
              <td :colspan="visibleColumns.length + 1" class="empty-cell">暂无已提交任务</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="pagination-row">
        <div class="pagination-info">共 {{ totalTasks }} 条记录</div>
        <div class="pagination-controls">
          <div class="page-size-selector">
            <span>每页</span>
            <select v-model="pageSize" class="page-size-input form-control">
              <option :value="20">20</option>
              <option :value="50">50</option>
              <option :value="75">75</option>
              <option :value="100">100</option>
            </select>
            <span>条</span>
          </div>
          <button @click="prevPage" :disabled="currentPage === 1" class="btn btn-default btn-sm pagination-btn">
            <i class="fas fa-chevron-left"></i>
          </button>
          <div class="page-numbers">
            <button v-for="page in visiblePages" :key="page" @click="goToPage(page)"
              :class="['page-btn', { active: currentPage === page, disabled: page === '...' }]" :disabled="page === '...'">
              {{ page }}
            </button>
          </div>
          <button @click="nextPage" :disabled="currentPage >= totalPages" class="btn btn-default btn-sm pagination-btn">
            <i class="fas fa-chevron-right"></i>
          </button>
          <div class="page-jump">
            <span>跳转到</span>
            <input type="number" v-model="jumpPage" min="1" :max="totalPages" class="jump-input" @keyup.enter="handleJump">
            <span>页</span>
            <button @click="handleJump" class="btn btn-primary btn-sm jump-btn">GO</button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showColumnSelector" class="column-selector-modal" @click.self="toggleColumnSelector">
      <div class="column-selector-content">
        <div class="column-selector-header">
          <h4>列设置</h4>
          <button @click="toggleColumnSelector" class="close-btn"><i class="fas fa-times"></i></button>
        </div>
        <div class="column-selector-body">
          <label v-for="column in allColumns" :key="column.key" class="column-checkbox">
            <input type="checkbox" v-model="column.visible" @change="updateVisibleColumns">
            <span>{{ column.label }}</span>
          </label>
        </div>
        <div class="column-selector-footer">
          <button @click="resetColumns" class="btn btn-default">重置</button>
          <button @click="toggleColumnSelector" class="btn btn-primary">确定</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { getAlreadyInitiated, retrieveTask, buildPagedRequest } from '../api/task'

const router = useRouter()

const showColumnSelector = ref(false)
const currentPage = ref(1)
const pageSize = ref(20)
const sortColumn = ref('operationTime')
const sortOrder = ref('desc')
const jumpPage = ref(1)

const searchForm = reactive({
  processName: '', taskNo: '', summary: '', priority: '',
  operationTimeStart: '', operationTimeEnd: '', serialNo: '',
  initiator: '', initiateTimeStart: '', initiateTimeEnd: '', status: ''
})

const allColumns = ref([
  { key: 'taskNo', label: '任务号', sortable: true, visible: true, width: '100px' },
  { key: 'serialNo', label: '流水号', sortable: true, visible: true, width: '160px' },
  { key: 'processName', label: '流程名', sortable: true, visible: true, width: '200px' },
  { key: 'initiator', label: '发起人', sortable: true, visible: true, width: '100px' },
  { key: 'initiateTime', label: '发起时间', sortable: true, visible: true, width: '180px' },
  { key: 'status', label: '状态', sortable: false, visible: true, width: '160px' },
  { key: 'summary', label: '摘要信息', sortable: false, visible: true, width: '250px' },
  { key: 'priority', label: '优先级', sortable: false, visible: true, width: '80px' }
])

const tasks = ref([])

const fmt = (v) => {
  if (v === undefined || v === null) return ''
  return String(v).replace('T', ' ').substring(0, 19)
}
const priorityName = (p) => { const s = (p ?? '').toString(); if (['高','中','低'].includes(s)) return s; const n = Number(p); return n === 2 ? '高' : n === 0 ? '低' : '中' }
const mapTask = (t) => ({
  id: t.taskId ?? t.TaskId,
  taskNo: String(t.taskId ?? t.TaskId ?? ''),
  serialNo: t.serialNum || t.SerialNum || '',
  processName: t.processName || t.ProcessName || '',
  initiator: t.initiatorName || t.InitiatorName || t.initiator || t.Initiator || '',
  initiateTime: fmt(t.createAt || t.CreateAt),
  operationTime: fmt(t.finishOn ?? t.FinishOn),
  status: t.statusName || t.StatusName || '',
  statusCode: t.status ?? t.Status ?? 0,
  summary: t.para0 || t.Para0 || '',
  priority: priorityName(t.priority ?? t.Priority),
  _raw: t,
  formGuid: t.formGuid || t.FormGuid || '',
  formType: t.formType || t.FormType || t.designerType || t.DesignerType || '2',
  formData: t.formData || t.FormData || {},
  flowGuid: t.flowGuid || t.FlowGuid || t.processGuid || t.ProcessGuid || ''
})

const loadTasks = async () => {
  try {
    const res = await getAlreadyInitiated(buildPagedRequest({ pageNumber: 0, pageSize: 500, searchText: searchForm.processName || '' }))
    tasks.value = (res.rows || res.Rows || []).map(mapTask)
  } catch (e) {
    console.error('加载已发起任务失败', e)
  }
}

onMounted(loadTasks)

const visibleColumns = computed(() => allColumns.value.filter(col => col.visible))
const processNameOptions = computed(() => [...new Set(tasks.value.map(t => t.processName).filter(Boolean))].sort())
const onProcessNameFocus = () => {}

const filteredTasks = computed(() => {
  let result = [...tasks.value]
  if (searchForm.processName) result = result.filter(t => t.processName.toLowerCase().includes(searchForm.processName.toLowerCase()))
  if (searchForm.taskNo) result = result.filter(t => t.taskNo.includes(searchForm.taskNo))
  if (searchForm.summary) result = result.filter(t => t.summary.toLowerCase().includes(searchForm.summary.toLowerCase()))
  if (searchForm.priority) result = result.filter(t => t.priority === searchForm.priority)
  if (searchForm.serialNo) result = result.filter(t => t.serialNo.includes(searchForm.serialNo))
  if (searchForm.initiator) result = result.filter(t => t.initiator.toLowerCase().includes(searchForm.initiator.toLowerCase()))
  if (searchForm.status) result = result.filter(t => String(t.statusCode) === searchForm.status)
  if (searchForm.initiateTimeStart) result = result.filter(t => t.initiateTime >= searchForm.initiateTimeStart)
  if (searchForm.initiateTimeEnd) result = result.filter(t => t.initiateTime <= searchForm.initiateTimeEnd + ' 23:59:59')
  if (searchForm.operationTimeStart) result = result.filter(t => t.operationTime >= searchForm.operationTimeStart)
  if (searchForm.operationTimeEnd) result = result.filter(t => t.operationTime <= searchForm.operationTimeEnd + ' 23:59:59')
  if (sortColumn.value && sortColumn.value !== 'status' && sortColumn.value !== 'priority' && sortColumn.value !== 'summary') {
    result.sort((a, b) => {
      const aVal = a[sortColumn.value], bVal = b[sortColumn.value]
      if (aVal < bVal) return sortOrder.value === 'asc' ? -1 : 1
      if (aVal > bVal) return sortOrder.value === 'asc' ? 1 : -1
      return 0
    })
  }
  return result
})

const totalTasks = computed(() => filteredTasks.value.length)
const totalPages = computed(() => Math.ceil(totalTasks.value / pageSize.value) || 1)
const paginatedTasks = computed(() => { const s = (currentPage.value - 1) * pageSize.value; return filteredTasks.value.slice(s, s + pageSize.value) })

const visiblePages = computed(() => {
  const pages = [], total = totalPages.value, current = currentPage.value
  if (total <= 7) { for (let i = 1; i <= total; i++) pages.push(i) }
  else { pages.push(1); if (current > 4) pages.push('...'); for (let i = Math.max(2, current - 2); i <= Math.min(total - 1, current + 2); i++) pages.push(i); if (current < total - 3) pages.push('...'); pages.push(total) }
  return pages
})

const statusCodeColorMap = { 0: 'processing', 1: 'cc', 2: 'rejected', 3: 'rejected', 4: 'approved', 5: 'rejected', 6: 'default', 10014: 'cc', 10025: 'cc', 10018: 'cc', 10003: 'rejected' }
const getStatusClass = (task) => {
  const code = task.statusCode ?? 0
  if (statusCodeColorMap[code] !== undefined) return statusCodeColorMap[code]
  const s = (task.status || '').toString()
  if (s.includes('[')) return 'processing'
  return 'default'
}

const canRetrieve = (task) => {
  return task.statusCode === 0
}

const canDelete = (task) => {
  return task.statusCode === 0
}

const handleSort = (column) => {
  if (allColumns.value.find(c => c.key === column && !c.sortable)) return
  if (sortColumn.value === column) { sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc' }
  else { sortColumn.value = column; sortOrder.value = 'asc' }
}

const handleSearch = () => { currentPage.value = 1; loadTasks() }
const resetSearch = () => { Object.keys(searchForm).forEach(k => searchForm[k] = ''); currentPage.value = 1 }
const prevPage = () => { if (currentPage.value > 1) currentPage.value-- }
const nextPage = () => { if (currentPage.value < totalPages.value) currentPage.value++ }
const goToPage = (p) => { if (typeof p === 'number' && p >= 1 && p <= totalPages.value) currentPage.value = p }
const handleJump = () => { const p = parseInt(jumpPage.value); if (p >= 1 && p <= totalPages.value) currentPage.value = p; else jumpPage.value = currentPage.value }
const toggleColumnSelector = () => { showColumnSelector.value = !showColumnSelector.value }
const updateVisibleColumns = () => { if (allColumns.value.filter(c => c.visible).length < 1) { const h = allColumns.value.find(c => !c.visible); if (h) h.visible = true } }
const resetColumns = () => { allColumns.value.forEach(c => c.visible = true) }

const exportTable = () => {
  const cols = visibleColumns.value
  const header = cols.map(c => c.label).join(',')
  const rows = filteredTasks.value.map(t => cols.map(c => {
    let v = t[c.key] || ''
    if (typeof v === 'string' && (v.includes(',') || v.includes('"') || v.includes('\n'))) { v = '"' + v.replace(/"/g, '""') + '"' }
    return v
  }).join(','))
  const csv = '\uFEFF' + header + '\n' + rows.join('\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url; a.download = '已发起任务.csv'; a.click()
  URL.revokeObjectURL(url)
}

let dragSrcColumn = null
const dragStart = (e, col) => { dragSrcColumn = col; e.dataTransfer.effectAllowed = 'move' }
const dropColumn = (e, targetCol) => {
  e.preventDefault()
  if (!dragSrcColumn || dragSrcColumn.key === targetCol.key) return
  const srcIdx = allColumns.value.indexOf(dragSrcColumn)
  const tgtIdx = allColumns.value.indexOf(targetCol)
  allColumns.value.splice(srcIdx, 1)
  allColumns.value.splice(tgtIdx, 0, dragSrcColumn)
}

const retrieveTaskHandler = async (task) => {
  if (!await $confirm(`确认取回任务: ${task.taskNo} - ${task.processName}?`)) return
  try {
    const raw = task._raw || {}
    const res = await retrieveTask({
      taskId: raw.TaskId || task.taskNo,
      stepId: raw.stepId || 0
    })
    if (res && res.resultType) {
      $message.success(`已取回任务: ${task.taskNo}`)
      loadTasks()
    } else {
      $message.error((res && res.message) || '取回失败')
    }
  } catch (e) {
    $message.error('取回失败: ' + (e.message || '未知错误'))
  }
}
const deleteTask = async (task) => { if (!await $confirm(`确定删除任务 "${task.taskNo} - ${task.processName}" 吗？`)) return }
const viewTask = (task) => {
  window.dispatchEvent(new CustomEvent('navigateToTaskView', {
    detail: {
      taskId: task.taskNo || task.id || task.TaskNo,
      mode: 'view', // 已发起任务：查看模式
      type: 0,
      processName: task.processName || task.ProcessName || '',
      title: `任务-${task.processName || task.ProcessName || task.taskNo || task.id}`,
      taskData: task
    }
  }))
}
</script>

<style scoped>
.view-container { padding: 20px; background: #fff; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.05); display: flex; flex-direction: column; max-height: calc(100vh - 150px); overflow: hidden; }
.search-panel { background: #fff; border: 1px solid #e0e4e8; border-radius: 8px; padding: 20px; margin-bottom: 15px; flex-shrink: 0; box-shadow: 0 1px 4px rgba(0,0,0,0.04); }
.search-row { display: flex; gap: 16px; align-items: flex-end; flex-wrap: wrap; }
.search-row + .search-row { margin-top: 12px; }
.search-item { display: flex; align-items: center; gap: 8px; white-space: nowrap; }
.search-item label { font-size: 13px; color: #6b7280; font-weight: 500; }
.search-item label i { margin-right: 2px; color: #1ab394; font-size: 12px; }
.search-item .form-control { padding: 8px 12px; border: 1px solid #d1d5db; border-radius: 6px; font-size: 13px; outline: none; background: #fafbfc; }
.search-item .form-control:focus { border-color: #1ab394; box-shadow: 0 0 0 3px rgba(26,179,148,0.12); background: #fff; }
.search-item input[type="text"] { width: 160px; }
.search-item select { width: 140px; }
.search-item input[type="date"] { width: 130px; }
.date-separator { color: #9ca3af; margin: 0 6px; }
.search-actions { display: flex; gap: 10px; margin-left: auto; align-self: flex-end; }
.search-actions .btn { padding: 8px 18px; font-size: 13px; border-radius: 6px; font-weight: 500; display: inline-flex; align-items: center; gap: 5px; border: none; outline: none; box-shadow: none; }
.search-actions .btn-primary { background-color: #1ab394; color: #fff; }
.search-actions .btn-primary:hover { background-color: #18a689; }
.search-actions .btn-default { background-color: #f3f3f4; color: #676a6c; border: 1px solid #e7eaec; }
.search-actions .btn-default:hover { background-color: #e7eaec; }
.search-actions .btn-success { background-color: #1ab394; color: #fff; }
.search-actions .btn-success:hover { background-color: #18a689; }
.table-wrapper { border: 1px solid #e7eaec; border-radius: 4px; overflow: hidden; display: flex; flex-direction: column; flex: 1; min-height: 0; }
.table-header-row { display: flex; justify-content: space-between; align-items: center; padding: 10px 15px; background-color: #f9f9f9; border-bottom: 1px solid #e7eaec; flex-shrink: 0; }
.table-header-left { display: flex; gap: 8px; }
.table-header-right { display: flex; gap: 8px; }
.column-settings-btn { padding: 4px 10px; font-size: 12px; }
.table-container { overflow-y: auto; flex: 1; }
.data-table { width: 100%; border-collapse: collapse; table-layout: fixed; }
.data-table th, .data-table td { padding: 10px 15px; text-align: left; border-bottom: 1px solid #e7eaec; white-space: nowrap; font-size: 13px; color: #676a6c; overflow: hidden; text-overflow: ellipsis; }
.data-table th { background-color: #f5f5f5; font-weight: 600; color: #676a6c; font-size: 13px; position: sticky; top: 0; z-index: 2; }
.data-table tbody tr:hover { background: #f9f9f9; }.task-row { cursor: pointer; }
.column-header { cursor: pointer; user-select: none; white-space: nowrap; }
.column-header:hover { background-color: #e7eaec; }
.column-header[draggable="true"]:active { cursor: grabbing; opacity: 0.6; }
.column-header i { margin-left: 4px; color: #9ca3af; font-size: 11px; vertical-align: middle; }
.sorted-asc, .sorted-desc { background-color: #ecfdf5; }
.summary-cell { max-width: 200px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; display: inline-block; vertical-align: middle; }
.status-badge { display: inline-block; padding: 2px 10px; border-radius: 10px; font-size: 12px; font-weight: 500; }
.status-badge.processing { background-color: #eff6ff; color: #2563eb; border: 1px solid #bfdbfe; }
.status-badge.approved { background-color: #f0fdf4; color: #16a34a; border: 1px solid #bbf7d0; }
.status-badge.rejected { background-color: #fef2f2; color: #dc2626; border: 1px solid #fecaca; }
.status-badge.cc { background-color: #fefce8; color: #ca8a04; border: 1px solid #fef08a; }
.status-badge.default { background-color: #f9fafb; color: #6b7280; border: 1px solid #e5e7eb; }
.priority-badge { display: inline-block; padding: 2px 8px; border-radius: 3px; font-size: 12px; font-weight: 500; }
.priority-badge.high { background-color: #fee2e2; color: #dc2626; }
.priority-badge.medium { background-color: #dbeafe; color: #2563eb; }
.priority-badge.low { background-color: #dcfce7; color: #16a34a; }
.action-column { white-space: nowrap; }
.action-column .btn-xs { margin-right: 4px; padding: 4px 8px; font-size: 12px; border-radius: 4px; cursor: pointer; border: none; transition: all 0.2s; color: #fff; }
.action-column .btn-info.btn-xs { background-color: #31b0d5; }
.action-column .btn-warning.btn-xs { background-color: #f0ad4e; }
.action-column .btn-danger.btn-xs { background-color: #d9534f; }
.empty-cell { text-align: center; padding: 40px; color: #9ca3af; }
.pagination-row { display: flex; justify-content: space-between; align-items: center; padding: 10px 15px; background: #f9f9f9; border-top: 1px solid #e7eaec; flex-shrink: 0; }
.pagination-info { font-size: 13px; color: #676a6c; }
.pagination-controls { display: flex; align-items: center; gap: 10px; }
.page-numbers { display: flex; gap: 5px; }
.page-btn { width: 32px; height: 32px; border: 1px solid #e7eaec; background-color: #fff; color: #676a6c; border-radius: 4px; cursor: pointer; font-size: 13px; display: flex; align-items: center; justify-content: center; transition: all 0.2s ease; }
.page-btn:hover:not(.disabled) { background-color: #f9f9f9; border-color: #1ab394; color: #1ab394; }
.page-btn.active { background-color: #1ab394; border-color: #1ab394; color: #fff; }
.page-btn.disabled { cursor: not-allowed; opacity: 0.5; }
.pagination-btn { width: 32px; height: 32px; padding: 0; display: flex; align-items: center; justify-content: center; }
.page-size-selector { display: flex; align-items: center; gap: 5px; font-size: 13px; margin-right: 10px; padding-right: 10px; border-right: 1px solid #e7eaec; }
.page-size-selector span { font-size: 13px; color: #676a6c; }
.page-size-input { width: 60px; padding: 5px 8px; font-size: 13px; }
.jump-input { width: 50px; padding: 5px 8px; border: 1px solid #e7eaec; border-radius: 4px; font-size: 13px; text-align: center; }
.jump-input:focus { border-color: #1ab394; box-shadow: 0 0 0 3px rgba(26,179,148,0.12); outline: none; }
.jump-btn { padding: 5px 12px; }
.column-selector-modal { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background-color: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1000; }
.column-selector-content { background-color: #fff; border-radius: 6px; width: 350px; }
.column-selector-header { display: flex; justify-content: space-between; align-items: center; padding: 15px 20px; border-bottom: 1px solid #e7eaec; }
.column-selector-header h4 { margin: 0; font-size: 14px; }
.close-btn { background: none; border: none; cursor: pointer; color: #676a6c; font-size: 16px; }
.close-btn:hover { color: #333; }
.column-selector-body { padding: 15px; max-height: 300px; overflow-y: auto; display: flex; flex-direction: column; gap: 12px; }
.column-checkbox { display: flex; align-items: center; gap: 10px; cursor: pointer; font-size: 13px; }
.column-checkbox input[type="checkbox"] { width: 16px; height: 16px; cursor: pointer; }
.column-selector-footer { padding: 12px 20px; border-top: 1px solid #e7eaec; display: flex; justify-content: flex-end; gap: 10px; }
.column-selector-footer .btn { padding: 6px 15px; font-size: 13px; border-radius: 4px; cursor: pointer; display: inline-flex; align-items: center; gap: 5px; border: none; outline: none; box-shadow: none; }
.column-selector-footer .btn-default { background-color: #f3f3f4; color: #676a6c; border: 1px solid #e7eaec; }
.column-selector-footer .btn-default:hover { background-color: #e7eaec; }
.column-selector-footer .btn-primary { background-color: #1ab394; color: #fff; }
.column-selector-footer .btn-primary:hover { background-color: #18a689; }
</style>